import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import {
  ensureAuthorized,
  getAccessToken,
  redirectToAuthorize,
  rememberPendingNumber,
  consumePendingNumber,
} from '../lib/ringcentral/auth';
import { ensureWebPhone, placeOutboundCall } from '../lib/ringcentral/webphone';

const normalizeToE164 = (raw) => {
  if (!raw) return null;
  const digits = String(raw).replace(/[^\d]/g, '');
  if (digits.length === 0) return null;
  // Default to US if no country code
  const withCountry =
    digits.length === 10
      ? `+1${digits}`
      : digits.startsWith('1') && digits.length === 11
        ? `+${digits}`
        : digits.startsWith('+')
          ? digits
          : `+${digits}`;
  return withCountry;
};

export const useRingCentralCall = () => {
  const [isPlacing, setIsPlacing] = useState(false);

  // `fromNumber` (optional): per-surface caller ID override — the Onboarding
  // board presents the LOGGED-IN person's own direct line (personal-numbers
  // round 2026-08-10); every other surface keeps the app default.
  const startCallTo = useCallback(async (rawNumber, fromNumber) => {
    const e164 = normalizeToE164(rawNumber);
    if (!e164) {
      toast.error('Invalid phone number');
      return;
    }
    // Caller ID must be E.164 too: RC silently REJECTS a formatted number
    // like "(615) 263-9391" and falls back to the extension's default
    // caller ID (learned live 2026-08-10) — normalize instead of trusting
    // however the number was typed into the user profile.
    const fromE164 = normalizeToE164(fromNumber) || undefined;

    try {
      setIsPlacing(true);
      // Prompt mic permission early to avoid late failures and autoplay issues
      try {
        const permissionStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        // Immediately release the mic from the permission prompt stream
        try {
          permissionStream.getTracks().forEach((t) => t.stop());
        } catch (_) {}
      } catch (micErr) {
        toast.error('Microphone permission is required to place calls');
        throw micErr;
      }
      const authorized = await ensureAuthorized();
      if (!authorized) {
        rememberPendingNumber(e164);
        redirectToAuthorize();
        return;
      }

      const token = getAccessToken();
      const webPhone = await ensureWebPhone(token);
      // Returned so callers can read call metadata off the live session —
      // the Onboarding board captures session.sessionId (RC telephony
      // session id) to tag page-dialed calls in the step record.
      return await placeOutboundCall(webPhone, e164, fromE164);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      toast.error(err.message || 'Unable to start the call');
    } finally {
      setIsPlacing(false);
    }
  }, []);

  const tryCallPending = useCallback(async () => {
    const pending = consumePendingNumber();
    if (!pending) return false;
    try {
      await startCallTo(pending);
      return true;
    } catch (e) {
      return false;
    }
  }, [startCallTo]);

  return { isPlacing, startCallTo, tryCallPending };
};
